import React from 'react'
import { CardHeader, Card, CardContent, Typography, Box } from '@mui/material'
import { TextFieldElement, useWatch } from 'react-hook-form-mui'
import type { FieldValues, TextFieldElementProps } from 'react-hook-form-mui'
import { InvalidQuestionError, OptionalLabel } from '../../styles'
import QuestionHeader from '../../../StudentSurveyListFeature/components/QuestionHeader'
import { grey } from '../../../../colors'
import { FREE_RESPONSE_MAX_LENGTH } from '../../constants'

interface FreeResponseCardProps extends TextFieldElementProps<FieldValues> {
  title: string
  isRequired: boolean
  questionNumber: number
  invalidQuestionItemId: string
}

const FreeResponseCard: React.FC<FreeResponseCardProps> = (
  {
    key,
    name,
    title,
    disabled = false,
    questionNumber,
    isRequired,
    invalidQuestionItemId,
    ...rest
  }

): JSX.Element => {
  const currentValue = useWatch({ name }) as string | undefined
  const characterCount = currentValue?.length ?? 0
  const isOverLimit = characterCount > FREE_RESPONSE_MAX_LENGTH
  const isNearLimit = characterCount > FREE_RESPONSE_MAX_LENGTH * 0.9
  const isInvalid = invalidQuestionItemId && invalidQuestionItemId === name
  const isCharacterLimitError = isInvalid && isOverLimit
  const isRequiredError = isInvalid && !isOverLimit

  return (
    <Box tabIndex={0} aria-label={`Survey Question ${questionNumber} ${title}`} id={name} sx={{ p: '14px 18px', border: `1px solid ${grey[200]} `, borderRadius: '10px' }}>
      <QuestionHeader isRequired={isRequired} name={name} />
      {isCharacterLimitError && <InvalidQuestionError>*Response exceeds the maximum character limit of {FREE_RESPONSE_MAX_LENGTH}. Please shorten your response.</InvalidQuestionError>}
      {isRequiredError && <InvalidQuestionError>*Response required. Please answer and submit again.</InvalidQuestionError>}
      <label htmlFor={name}>
        <Typography variant="h5" component="h3" color={disabled ? 'text.secondary' : 'text.primary'} my={2} >
          {questionNumber}. {title}
        </Typography>
      </label>
      <TextFieldElement
        id={name}
        name={name}
        fullWidth
        placeholder="Type response here..."
        multiline
        inputProps={{ maxLength: FREE_RESPONSE_MAX_LENGTH }}
        {...rest}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: isOverLimit ? 'error.main' : isNearLimit ? 'error.main' : 'text.secondary',
            fontSize: '0.875rem'
          }}
        >
          {characterCount}/{FREE_RESPONSE_MAX_LENGTH}
        </Typography>
      </Box>
    </Box>
  )
}

export default FreeResponseCard
